import React, { useState } from 'react';
import { Menu, Search, User, LogOut, Settings, ChevronDown } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import NotificationBell from '../notifications/NotificationBell';
import { useNavigate } from 'react-router-dom';

const Topbar = ({ onMenuToggle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/companies?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4 md:px-8 shadow-sm">
      {/* Left: Mobile Menu & Search */}
      <div className="flex items-center space-x-4 flex-1 max-w-md">
        <button
          onClick={onMenuToggle}
          className="p-2 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-50 md:hidden"
        >
          <Menu className="w-6 h-6" />
        </button>

        <form onSubmit={handleSearchSubmit} className="relative w-full hidden sm:block">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search companies, admins, or ID prefixes..."
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition"
          />
        </form>
      </div>

      {/* Right Actions: Notifications & Profile */}
      <div className="flex items-center space-x-4">
        <NotificationBell />

        {/* Profile Dropdown */}
        <div className="relative">
          <button
            onClick={() => setProfileOpen(!profileOpen)}
            className="flex items-center space-x-2.5 p-1.5 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200 transition"
          >
            <img
              src={user?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80'}
              alt={user?.name}
              className="w-8 h-8 rounded-full object-cover shadow-sm border border-slate-100"
            />
            <span className="hidden sm:inline-block text-sm font-semibold text-slate-700">
              {user?.name}
            </span>
            <ChevronDown className="w-4 h-4 text-slate-400 hidden sm:inline-block" />
          </button>

          {profileOpen && (
            <div
              onMouseLeave={() => setProfileOpen(false)}
              className="absolute right-0 mt-2 w-56 rounded-2xl bg-white border border-slate-100 shadow-[0_10px_40px_rgb(0,0,0,0.08)] p-2 z-50 animate-fadeIn"
            >
              <div className="px-3 py-3 border-b border-slate-100 mb-2">
                <p className="text-sm font-bold text-slate-900">{user?.name}</p>
                <p className="text-xs text-slate-500 font-medium truncate mt-0.5">{user?.email}</p>
              </div>

              <button
                onClick={() => {
                  setProfileOpen(false);
                  navigate('/settings');
                }}
                className="w-full flex items-center space-x-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:text-green-700 hover:bg-green-50 transition"
              >
                <Settings className="w-4 h-4 text-green-600" />
                <span>Account Settings</span>
              </button>

              <button
                onClick={logout}
                className="w-full flex items-center space-x-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-rose-600 hover:bg-rose-50 transition mt-1"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
